import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Upload,
  Database,
  CheckCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import BoyceRunAllCard from './BoyceRunAllCard';
import BoyceJobsPanel from './BoyceJobsPanel';
import { chunkedUpload, makeUploadId } from '@/lib/chunkedUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dedicated Boyce (Auto Shop) CSV importer.
 *
 * Strategy:
 *   1. User picks .csv file.
 *   2. We POST to /api/super-admin/boyce-import/customers/preview to validate
 *      and show summary + sample mapped rows.
 *   3. User confirms → POST to /api/super-admin/boyce-import/customers to run
 *      the actual import (idempotent — re-runs are safe).
 */
export default function BoyceImportTab() {
  const [stats, setStats] = useState(null);
  const [fullStats, setFullStats] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);
  // Service-customers / Service-vehicles state
  const [scFile, setScFile] = useState(null);
  const [svFile, setSvFile] = useState(null);
  const [scImporting, setScImporting] = useState(false);
  const [svImporting, setSvImporting] = useState(false);
  const [scResult, setScResult] = useState(null);
  const [svResult, setSvResult] = useState(null);
  // Invoices (step 4)
  const [invServicesFile, setInvServicesFile] = useState(null);
  const [invHeadersFile, setInvHeadersFile] = useState(null);
  const [invLinesFile, setInvLinesFile] = useState(null);
  const [invImporting, setInvImporting] = useState(false);
  const [invResult, setInvResult] = useState(null);
  const [invJobId, setInvJobId] = useState(null);
  const [invJobState, setInvJobState] = useState(null);
  // Per-file chunked-upload progress: { services: {current, total}, ... }
  const [invUploadProgress, setInvUploadProgress] = useState({});
  const invPollRef = useRef(null);
  const fileRef = useRef(null);
  const scFileRef = useRef(null);
  const svFileRef = useRef(null);
  const invServicesRef = useRef(null);
  const invHeadersRef = useRef(null);
  const invLinesRef = useRef(null);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  useEffect(() => () => {
    if (invPollRef.current) clearInterval(invPollRef.current);
  }, []);

  const authHeader = () => {
    const session = localStorage.getItem('session_token');
    return session ? { Authorization: `Bearer ${session}` } : {};
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/stats`, {
        headers: authHeader(),
      });
      if (res.ok) setStats(await res.json());
      const fullRes = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/full-stats`, {
        headers: authHeader(),
      });
      if (fullRes.ok) setFullStats(await fullRes.json());
    } catch (_e) {/* noop */}
  };

  useEffect(() => { loadStats(); }, []);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a .csv file exported from Boyce');
      return;
    }
    setFile(f);
    setPreview(null);
    setResult(null);
  };

  const runPreview = async () => {
    if (!file) return;
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/customers/preview`, {
        method: 'POST', headers: authHeader(), body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      setPreview(await res.json());
    } catch (err) {
      toast.error(`Preview failed: ${err.message || err}`);
    } finally {
      setPreviewing(false);
    }
  };

  const runImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('duplicate_action', 'update');
      const res = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/customers`, {
        method: 'POST', headers: authHeader(), body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setResult(data);
      toast.success(`Imported ${data.imported} new, updated ${data.updated}, skipped ${data.skipped}`);
      await loadStats();
    } catch (err) {
      toast.error(`Import failed: ${err.message || err}`);
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-6" data-testid="boyce-import-tab">
      {/* Hero / intro */}
      <Card className="p-6 bg-gradient-to-br from-slate-900 to-slate-700 text-slate-50 border-0">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-white/10">
            <Database className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold">Boyce Auto Shop — Customer Import</h2>
            <p className="text-slate-300 text-sm mt-1 max-w-2xl">
              Direct CSV import from your Boyce export. Auto-normalises phone numbers, names, and
              addresses, and is fully idempotent — re-uploads only update changed fields. Dedup
              priority: <span className="font-mono">boyce_id → email → mobile → phone</span>.
            </p>
          </div>
        </div>
      </Card>

      {/* Quick Start — one-shot uploader (the recommended path) */}
      <BoyceRunAllCard onComplete={() => loadStats()} />

      {/* Recent Import Jobs — debug panel showing run-all + invoices-only failures */}
      <BoyceJobsPanel />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="boyce-stats">
          <Card className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Total customers (all sources)</p>
            <p className="text-2xl font-semibold text-emerald-600 mt-1">{stats.total_customers}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">From Boyce master list</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">{stats.boyce_customers}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">With mobile</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">{stats.with_mobile}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">With email</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">{stats.with_email}</p>
          </Card>
        </div>
      )}

      {/* Upload */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <Upload className="h-5 w-5 text-slate-500 mt-1" />
          <div className="flex-1">
            <h3 className="font-medium text-slate-900">Step 1 · Customer master CSV</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Export `Customers` from Boyce as a CSV. The importer expects columns: Id, FirstName,
              LastName, DisplayName, Email, Mobile, Phone, Address, Suburb, State, Postcode,
              Country, Title, Company, Marketing, AbnNo, IsDefault, Notes, Comment.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                onChange={handleFile}
                className="text-sm"
                data-testid="boyce-file-input"
              />
              {file && (
                <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </Badge>
              )}
              <div className="flex-1" />
              {file && !preview && (
                <Button
                  onClick={runPreview}
                  disabled={previewing}
                  variant="secondary"
                  data-testid="boyce-preview-btn"
                >
                  {previewing
                    ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analysing…</>)
                    : (<><Sparkles className="h-4 w-4 mr-2" />Preview & Validate</>)}
                </Button>
              )}
              {(file || preview) && (
                <Button variant="ghost" onClick={reset} data-testid="boyce-reset-btn">
                  <RefreshCw className="h-4 w-4 mr-2" /> Reset
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Preview */}
      {preview && (
        <Card className="p-6 space-y-4" data-testid="boyce-preview-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="font-medium text-slate-900 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
                2. Validation summary
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                File parsed and mapped to AutoShopFlow's customer model.
              </p>
            </div>
            <Button
              onClick={runImport}
              disabled={importing}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="boyce-import-btn"
            >
              {importing
                ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</>)
                : (<><Database className="h-4 w-4 mr-2" />Import {preview.valid_rows} customers</>)}
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Total rows" value={preview.total_rows} />
            <Stat label="Valid" value={preview.valid_rows} tone="emerald" />
            <Stat label="Malformed" value={preview.malformed_rows} tone={preview.malformed_rows ? 'amber' : 'slate'} />
            <Stat label="Already in DB" value={preview.existing_in_db} tone={preview.existing_in_db ? 'blue' : 'slate'} />
            <Stat label="With mobile" value={preview.stats?.with_mobile} />
          </div>

          {preview.preview?.length > 0 && (
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Display Name</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Suburb</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Postcode</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.preview.map((c) => (
                    <TableRow key={c.boyce_id}>
                      <TableCell className="font-medium">{c.display_name}</TableCell>
                      <TableCell className="font-mono text-sm">{c.mobile || '—'}</TableCell>
                      <TableCell className="text-slate-600">{c.email || '—'}</TableCell>
                      <TableCell>{c.suburb || '—'}</TableCell>
                      <TableCell>{c.state || '—'}</TableCell>
                      <TableCell>{c.postcode || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="text-xs text-slate-500">
            Showing the first {preview.preview?.length || 0} of {preview.valid_rows} valid rows after normalisation.
          </p>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card className="p-6 border-emerald-200 bg-emerald-50/40" data-testid="boyce-result-panel">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-emerald-600 mt-1" />
            <div className="flex-1">
              <h3 className="font-medium text-slate-900">Import complete</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <Stat label="Inserted" value={result.imported} tone="emerald" />
                <Stat label="Updated" value={result.updated} tone="blue" />
                <Stat label="Skipped" value={result.skipped} />
                <Stat label="Malformed" value={result.malformed_rows} tone={result.malformed_rows ? 'amber' : 'slate'} />
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" /> {result.errors.length} row error(s):
                  </p>
                  <ul className="text-xs text-slate-600 list-disc pl-6 mt-1 space-y-0.5">
                    {result.errors.slice(0, 10).map((e, i) => (
                      <li key={i}>Row {e.row}: {e.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ==================== STEP 2 + 3: SERVICE CUSTOMERS & VEHICLES ==================== */}
      <Card className="p-6 border-slate-200">
        <h3 className="font-medium text-slate-900 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-500" />
          Step 2 + 3 · Service Customers and Vehicles
        </h3>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Upload the <span className="font-mono">HoppersServiceCustomers.csv</span> first, then
          <span className="font-mono"> HoppersServiceVehicles.csv</span>. Service Customers builds
          the <span className="font-medium">ServiceId → Customer</span> link (auto-creating stub
          customers for historical IDs not in your active list). Service Vehicles uses that link
          to attach each vehicle to its most-recent customer.
        </p>

        {fullStats && (
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mt-4" data-testid="boyce-full-stats">
            <Stat label="Master list (active)" value={fullStats.customers?.boyce} tone="emerald" />
            <Stat label="From history" value={fullStats.customers?.boyce_inferred} tone="blue" />
            <Stat label="Total customers" value={fullStats.customers?.total} tone="emerald" />
            <Stat label="Vehicles" value={fullStats.vehicles?.total} tone="emerald" />
            <Stat label="Invoices" value={fullStats.invoices?.boyce} tone="emerald" />
            <Stat label="Cust. snapshots" value={fullStats.service_snapshots?.customers} />
            <Stat label="Veh. snapshots" value={fullStats.service_snapshots?.vehicles} />
          </div>
        )}

        {/* Step 2: Service Customers */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-medium flex items-center justify-center mt-0.5">2</div>
            <div className="flex-1">
              <h4 className="font-medium text-slate-900">Service Customers CSV</h4>
              <p className="text-sm text-slate-500 mt-0.5">
                Required columns: ServiceId, CustomerId, Address, Email, Mobile, Phone, Suburb, State, Postcode.
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <input
                  ref={scFileRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => { setScFile(e.target.files?.[0] || null); setScResult(null); }}
                  className="text-sm"
                  data-testid="boyce-sc-file-input"
                />
                {scFile && (
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                    {scFile.name} · {(scFile.size / 1024).toFixed(0)} KB
                  </Badge>
                )}
                <div className="flex-1" />
                <Button
                  disabled={!scFile || scImporting}
                  onClick={async () => {
                    if (!scFile) return;
                    setScImporting(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', scFile);
                      const res = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/service-customers`, {
                        method: 'POST', headers: authHeader(), body: fd,
                      });
                      if (!res.ok) throw new Error(await res.text());
                      const data = await res.json();
                      setScResult(data);
                      toast.success(`Linked ${data.existing_customers_matched} active, created ${data.new_inferred_customers_created} historical stubs, stored ${data.snapshots_stored} snapshots`);
                      await loadStats();
                    } catch (err) {
                      toast.error(`Import failed: ${err.message || err}`);
                    } finally { setScImporting(false); }
                  }}
                  variant="secondary"
                  data-testid="boyce-sc-import-btn"
                >
                  {scImporting
                    ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</>)
                    : (<><Database className="h-4 w-4 mr-2" />Import Service Customers</>)}
                </Button>
              </div>
              {scResult && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                  <Stat label="Rows" value={scResult.total_rows} />
                  <Stat label="Unique customers" value={scResult.unique_customers} />
                  <Stat label="Matched active" value={scResult.existing_customers_matched} tone="emerald" />
                  <Stat label="Inferred created" value={scResult.new_inferred_customers_created} tone="blue" />
                  <Stat label="Snapshots stored" value={scResult.snapshots_stored} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step 3: Service Vehicles */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-medium flex items-center justify-center mt-0.5">3</div>
            <div className="flex-1">
              <h4 className="font-medium text-slate-900">Service Vehicles CSV</h4>
              <p className="text-sm text-slate-500 mt-0.5">
                Required columns: ServiceId, VehicleId, RegoNo, VinNo, LastServiced, NextServiceDate,
                Odometer, RegoExpiration, Name. Auto-parses make / model / year / engine capacity from Name.
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <input
                  ref={svFileRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => { setSvFile(e.target.files?.[0] || null); setSvResult(null); }}
                  className="text-sm"
                  data-testid="boyce-sv-file-input"
                />
                {svFile && (
                  <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                    {svFile.name} · {(svFile.size / 1024).toFixed(0)} KB
                  </Badge>
                )}
                <div className="flex-1" />
                <Button
                  disabled={!svFile || svImporting}
                  onClick={async () => {
                    if (!svFile) return;
                    setSvImporting(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', svFile);
                      const res = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/service-vehicles`, {
                        method: 'POST', headers: authHeader(), body: fd,
                      });
                      if (!res.ok) throw new Error(await res.text());
                      const data = await res.json();
                      setSvResult(data);
                      toast.success(`Created ${data.vehicles_created} vehicles, ${data.snapshots_stored} service snapshots stored`);
                      await loadStats();
                    } catch (err) {
                      toast.error(`Import failed: ${err.message || err}`);
                    } finally { setSvImporting(false); }
                  }}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="boyce-sv-import-btn"
                >
                  {svImporting
                    ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</>)
                    : (<><Database className="h-4 w-4 mr-2" />Import Service Vehicles</>)}
                </Button>
              </div>
              {svResult && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                  <Stat label="Rows" value={svResult.total_rows} />
                  <Stat label="Unique vehicles" value={svResult.unique_vehicles} />
                  <Stat label="Vehicles created" value={svResult.vehicles_created} tone="emerald" />
                  <Stat label="Snapshots stored" value={svResult.snapshots_stored} />
                  <Stat label="Unlinked" value={svResult.unlinked_skipped} tone={svResult.unlinked_skipped ? 'amber' : 'slate'} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step 4: Invoices (services + headers + line items) */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-medium flex items-center justify-center mt-0.5">4</div>
            <div className="flex-1">
              <h4 className="font-medium text-slate-900">Invoices (Service + Header + Line Items)</h4>
              <p className="text-sm text-slate-500 mt-0.5">
                Upload all three files together: <span className="font-mono">HoppersService.csv</span>,
                <span className="font-mono"> HoppersInvoiceHeader.csv</span> and
                <span className="font-mono"> HoppersLineItems.csv</span>. Each Boyce service becomes
                one invoice in AutoShopFlow (status = "paid" by default, "draft" when WasEstimated).
                Runs as a background job — browser stays responsive even on the full 58 MB dataset.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <FilePicker
                  label="HoppersService.csv"
                  inputRef={invServicesRef}
                  file={invServicesFile}
                  testid="boyce-inv-services"
                  onPick={(f) => { setInvServicesFile(f); setInvResult(null); }}
                />
                <FilePicker
                  label="HoppersInvoiceHeader.csv"
                  inputRef={invHeadersRef}
                  file={invHeadersFile}
                  testid="boyce-inv-headers"
                  onPick={(f) => { setInvHeadersFile(f); setInvResult(null); }}
                />
                <FilePicker
                  label="HoppersLineItems.csv"
                  inputRef={invLinesRef}
                  file={invLinesFile}
                  testid="boyce-inv-lines"
                  onPick={(f) => { setInvLinesFile(f); setInvResult(null); }}
                />
              </div>
              <div className="flex items-center justify-end gap-3 mt-3">
                <Button
                  disabled={!invServicesFile || !invHeadersFile || !invLinesFile || invImporting}
                  onClick={async () => {
                    setInvImporting(true);
                    setInvResult(null);
                    setInvJobState(null);
                    setInvJobId(null);
                    setInvUploadProgress({});
                    const uploadId = makeUploadId();
                    const chunkEndpoint = `${BACKEND_URL}/api/super-admin/boyce-import/chunk-upload`;
                    const triggerEndpoint = `${BACKEND_URL}/api/super-admin/boyce-import/invoices-from-staged`;
                    const onProgress = ({ fileKey, current, total }) => {
                      setInvUploadProgress((p) => ({ ...p, [fileKey]: { current, total } }));
                    };
                    try {
                      // 1) Upload three CSVs in chunks (sequentially)
                      await chunkedUpload({
                        file: invServicesFile, fileKey: 'services', uploadId,
                        endpoint: chunkEndpoint, authHeader: authHeader(), onProgress,
                      });
                      await chunkedUpload({
                        file: invHeadersFile, fileKey: 'headers', uploadId,
                        endpoint: chunkEndpoint, authHeader: authHeader(), onProgress,
                      });
                      await chunkedUpload({
                        file: invLinesFile, fileKey: 'line_items', uploadId,
                        endpoint: chunkEndpoint, authHeader: authHeader(), onProgress,
                      });
                      // 2) Trigger the staged-import job
                      const fd = new FormData();
                      fd.append('upload_id', uploadId);
                      const res = await fetch(triggerEndpoint, {
                        method: 'POST', headers: authHeader(), credentials: 'include', body: fd,
                      });
                      if (!res.ok) {
                        const errText = await res.text().catch(() => '');
                        throw new Error(errText || `HTTP ${res.status}`);
                      }
                      const data = await res.json();
                      setInvJobId(data.job_id);
                      toast.success(`Invoice import started · job ${data.job_id}`);
                      // 3) Poll status (reuse existing /invoices-async/{job_id} endpoint)
                      invPollRef.current = setInterval(async () => {
                        try {
                          const pr = await fetch(
                            `${BACKEND_URL}/api/super-admin/boyce-import/invoices-async/${data.job_id}`,
                            { headers: authHeader(), credentials: 'include' },
                          );
                          if (!pr.ok) return;
                          const j = await pr.json();
                          setInvJobState(j);
                          if (j.status === 'completed' || j.status === 'failed') {
                            clearInterval(invPollRef.current);
                            invPollRef.current = null;
                            setInvImporting(false);
                            if (j.status === 'completed') {
                              const result = j.progress?.invoices || {};
                              setInvResult(result);
                              toast.success(
                                `${(result.invoices_created || 0).toLocaleString()} invoices imported · $${(result.totals?.grand_total || 0).toLocaleString()} total`
                              );
                              await loadStats();
                            } else {
                              toast.error(
                                `Import failed at ${j.failed_step || 'invoices'}${j.failed_http_code ? ` · HTTP ${j.failed_http_code}` : ''} — see Recent Import Jobs panel for details`
                              );
                            }
                          }
                        } catch (_e) { /* keep polling */ }
                      }, 2500);
                    } catch (err) {
                      toast.error(`Upload failed: ${err.message || err}`);
                      setInvImporting(false);
                    }
                  }}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid="boyce-inv-import-btn"
                >
                  {invImporting
                    ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading & importing…</>)
                    : (<><Database className="h-4 w-4 mr-2" />Import Invoices</>)}
                </Button>
              </div>
              {invImporting && Object.keys(invUploadProgress).length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid="boyce-inv-upload-progress">
                  {['services', 'headers', 'line_items'].map((k) => {
                    const p = invUploadProgress[k];
                    if (!p) return null;
                    const pct = p.total > 0 ? Math.round((p.current / p.total) * 100) : 0;
                    const labels = { services: 'Service.csv', headers: 'InvoiceHeader.csv', line_items: 'LineItems.csv' };
                    return (
                      <div key={k} className="flex items-center gap-2 text-xs">
                        <span className="w-32 text-slate-600">{labels[k]}</span>
                        <div className="flex-1 h-1.5 bg-slate-200 rounded overflow-hidden">
                          <div
                            className={`h-full ${pct === 100 ? 'bg-emerald-500' : 'bg-blue-500'} transition-all`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-slate-500 w-20 text-right">{p.current}/{p.total} chunks</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {(invJobId || invJobState) && (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="boyce-inv-job-status">
                  {invJobId && <Badge variant="secondary" className="text-[10px] font-mono">Job: {invJobId}</Badge>}
                  {invJobState && (
                    <Badge variant="outline" className={
                      invJobState.status === 'completed' ? 'border-emerald-400 text-emerald-700'
                      : invJobState.status === 'failed' ? 'border-rose-400 text-rose-700'
                      : 'border-blue-400 text-blue-700'
                    }>
                      {invJobState.status === 'completed' ? 'Completed'
                        : invJobState.status === 'failed' ? `Failed: ${invJobState.failed_step || 'invoices'}`
                        : 'Running…'}
                    </Badge>
                  )}
                </div>
              )}
              {invResult && (
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Stat label="Invoices created" value={invResult.invoices_created?.toLocaleString()} tone="emerald" />
                    <Stat label="Replaced (re-run)" value={invResult.old_boyce_invoices_replaced?.toLocaleString()} tone="blue" />
                    <Stat label="Skipped (no vehicle)" value={invResult.skipped_no_vehicle} tone={invResult.skipped_no_vehicle ? 'amber' : 'slate'} />
                    <Stat label="No line items" value={invResult.invoices_with_no_line_items} />
                  </div>
                  {invResult.totals && (
                    <div className="grid grid-cols-3 gap-3">
                      <Stat label="Paid total" value={`$${invResult.totals.paid_value?.toLocaleString()}`} tone="emerald" />
                      <Stat label="Estimate value" value={`$${invResult.totals.estimate_value?.toLocaleString()}`} tone="amber" />
                      <Stat label="Grand total" value={`$${invResult.totals.grand_total?.toLocaleString()}`} tone="blue" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* ==================== STEP 5: Post-Import Auto-Cleanup ==================== */}
      <Card className="p-6 border-emerald-200 bg-emerald-50/30">
        <h3 className="font-medium text-slate-900 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-emerald-600" />
          Step 5 · Auto-Cleanup (run after uploading all CSVs)
        </h3>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          One-click idempotent fixer that runs the same migrations the preview environment already ran:
          (a) normalises every AU phone number to <code className="bg-white border px-1 rounded">0XXXXXXXXX</code>,
          (b) re-derives a useful display name for any customer still showing <em>Unknown (Boyce History)</em>,
          and (c) reconciles invoice <code className="bg-white border px-1 rounded">customer_name</code> snapshots.
          Safe to re-run.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            disabled={cleanupRunning}
            onClick={async () => {
              setCleanupRunning(true);
              setCleanupResult(null);
              try {
                const res = await fetch(`${BACKEND_URL}/api/super-admin/boyce-import/post-import-cleanup`, {
                  method: 'POST', headers: authHeader(),
                });
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();
                setCleanupResult(data);
                toast.success(`Cleanup done · ${data.customers_renamed.toLocaleString()} customers renamed · ${data.phone_fields_normalised.toLocaleString()} phones fixed`);
                await loadStats();
              } catch (err) {
                toast.error(`Cleanup failed: ${err.message || err}`);
              } finally {
                setCleanupRunning(false);
              }
            }}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="boyce-post-import-cleanup-btn"
          >
            {cleanupRunning ? 'Running cleanup…' : 'Run Post-Import Cleanup'}
          </Button>
          {cleanupResult && (
            <Badge variant="secondary" className={cleanupResult.remaining_unknown_customers === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}>
              {cleanupResult.remaining_unknown_customers === 0
                ? '✓ All customers have a name'
                : `${cleanupResult.remaining_unknown_customers} unnamed customers remaining`}
            </Badge>
          )}
        </div>
        {cleanupResult && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4" data-testid="boyce-cleanup-result">
            <Stat label="Phones normalised" value={cleanupResult.phone_fields_normalised?.toLocaleString()} tone="emerald" />
            <Stat label="Customers renamed" value={cleanupResult.customers_renamed?.toLocaleString()} tone="emerald" />
            <Stat label="From company" value={cleanupResult.rename_breakdown?.company?.toLocaleString()} />
            <Stat label="From snapshot" value={cleanupResult.rename_breakdown?.snapshot?.toLocaleString()} />
            <Stat label="From vehicle" value={cleanupResult.rename_breakdown?.vehicle?.toLocaleString()} />
            <Stat label="From phone" value={cleanupResult.rename_breakdown?.phone?.toLocaleString()} />
            <Stat label="Fallback" value={cleanupResult.rename_breakdown?.fallback?.toLocaleString()} />
            <Stat label="Invoices reconciled" value={cleanupResult.invoice_customer_names_reconciled?.toLocaleString()} tone="emerald" />
            <Stat label="Still unnamed" value={cleanupResult.remaining_unknown_customers?.toLocaleString()} tone={cleanupResult.remaining_unknown_customers === 0 ? 'emerald' : 'amber'} />
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value, tone = 'slate' }) {
  const tones = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
  };
  return (
    <div className="border rounded-lg p-3 bg-white">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-2xl font-semibold mt-1 ${tones[tone]}`}>{value ?? '—'}</p>
    </div>
  );
}

function FilePicker({ label, inputRef, file, onPick, testid }) {
  return (
    <div className="border rounded-lg p-3 bg-white">
      <p className="text-xs font-medium text-slate-600 mb-1.5">{label}</p>
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        onChange={(e) => onPick(e.target.files?.[0] || null)}
        className="text-xs w-full"
        data-testid={testid}
      />
      {file && (
        <p className="text-xs text-emerald-600 mt-1.5 truncate">
          ✓ {file.name} · {(file.size / 1024).toFixed(0)} KB
        </p>
      )}
    </div>
  );
}
